import { CandidateEditProfileEmpty, NewCVCandidateResponse } from 'candidate/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

import { AutocompleteV2ViewModel } from 'shared/components/autocomplete-v2';
import { MultiSelectViewModel } from 'shared/components';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';

type DirectoryOption = {
  id: number | string;
  name: string;
  slug?: string;
};

type SelectedOption = {
  id: number | string;
  name: string;
};

type VisibilityOption = {
  value: string;
  label: string;
};

type LanguageItemInitialValues = {
  id?: string;
  languageId?: string;
  skillId?: string;
};

type CompanySearchResponse = ApiResponseDto<DirectoryOption[]>;
type UserDetailWithCompanies = CandidateEditProfileEmpty['userdetail'] & {
  company_whitelist?: { value?: number | string | null; label?: string | null }[];
  company_blacklist?: { value?: number | string | null; label?: string | null }[];
};

function toOptionWithId(option: { value?: number | string | null; label?: string | null }): SelectedOption | null {
  const label = option.label ?? '';

  if (!label) {
    return null;
  }

  return {
    id: option.value ?? `custom:${label}`,
    name: label,
  };
}

function createVisibilityOption(option: { value?: number | string | null; label?: string | null }): VisibilityOption {
  return {
    value: String(option.value ?? ''),
    label: option.label ?? '',
  };
}

function matchesSearch(option: DirectoryOption, search: string): boolean {
  return option.name.toLowerCase().includes(search.trim().toLowerCase());
}

function uniqueSelected(options: SelectedOption[]): SelectedOption[] {
  const map = new Map<string, SelectedOption>();

  options.forEach((option) => {
    map.set(String(option.id), option);
  });

  return Array.from(map.values());
}

export class LanguageItem {
  readonly id: string;
  languageId = '';
  skillId = '';

  constructor(initialValues: LanguageItemInitialValues = {}) {
    this.id = initialValues.id ?? uuidv4();
    this.languageId = initialValues.languageId ?? '';
    this.skillId = initialValues.skillId ?? '';

    makeObservable(this, {
      languageId: observable,
      skillId: observable,
      valid: computed,
      hasValue: computed,
      setLanguageId: action.bound,
      setSkillId: action.bound,
    });
  }

  get hasValue(): boolean {
    return Boolean(this.languageId || this.skillId);
  }

  get valid(): boolean {
    return !this.hasValue || Boolean(this.languageId && this.skillId);
  }

  setLanguageId(value: string): void {
    this.languageId = value;
  }

  setSkillId(value: string): void {
    this.skillId = value;
  }
}

export class CompanyMultiAutocomplete {
  readonly selected = observable.array<SelectedOption>([]);
  readonly options = observable.array<DirectoryOption>([]);
  readonly autocompleteVM = new AutocompleteV2ViewModel<DirectoryOption>();
  readonly searchState = new AsyncState<CompanySearchResponse>();

  private searchRequestId = 0;
  private activeSearchRequestId: number | null = null;
  private readonly debouncedFetch = debounce((query: string) => {
    this.getCompanies(query);
  }, 500);

  constructor() {
    makeObservable(this, {
      selected: observable,
      options: observable,
      autocompleteOptions: computed,
      loading: computed,
      valid: computed,
      setSelected: action.bound,
      clear: action.bound,
      remove: action.bound,
      onSearch: action.bound,
      onSelect: action.bound,
      dispose: action.bound,
    });
  }

  get valid(): boolean {
    return this.selected.length > 0;
  }

  get loading(): boolean {
    return this.searchState.status === 'pending' && this.activeSearchRequestId !== null;
  }

  get autocompleteOptions(): DirectoryOption[] {
    const query = this.autocompleteVM.inputValue.trim();
    const options = this.options.slice();

    if (
      query.length >= 2 &&
      !options.some((option) => option.name.toLowerCase() === query.toLowerCase())
    ) {
      options.unshift({
        id: `custom:${query}`,
        name: query,
      });
    }

    return options.filter(
      (option) => !this.selected.some((selected) => String(selected.id) === String(option.id))
    );
  }

  setSelected(options: SelectedOption[]): void {
    this.selected.replace(uniqueSelected(options));
  }

  clear(): void {
    this.selected.clear();
    this.options.clear();
    this.autocompleteVM.clear();
  }

  remove(option: SelectedOption): void {
    const index = this.selected.findIndex((item) => String(item.id) === String(option.id));

    if (index >= 0) {
      this.selected.splice(index, 1);
    }
  }

  onSearch(query: string): void {
    this.autocompleteVM.setInputValue(query);

    if (query.length < 2) {
      this.debouncedFetch.cancel();
      this.searchRequestId += 1;
      this.activeSearchRequestId = null;
      this.options.clear();
      return;
    }

    this.debouncedFetch(query);
  }

  onSelect(option: DirectoryOption): void {
    this.debouncedFetch.cancel();
    this.searchRequestId += 1;
    this.activeSearchRequestId = null;
    this.selected.push({
      id: option.id,
      name: option.name,
    });
    this.options.clear();
    this.autocompleteVM.clear();
  }

  dispose(): void {
    this.debouncedFetch.cancel();
  }

  private async getCompanies(query: string): Promise<void> {
    const requestId = ++this.searchRequestId;
    this.activeSearchRequestId = requestId;

    await this.searchState.run(
      () => HttpClient.post<CompanySearchResponse>('/candidates/getCompanies', { query }),
      {
        onSuccess: (response) => {
          if (requestId !== this.activeSearchRequestId) {
            return;
          }

          runInAction(() => {
            this.options.replace(response.response ?? []);
          });
        },
        onError: () => {
          if (requestId !== this.activeSearchRequestId) {
            return;
          }

          runInAction(() => {
            this.options.replace([{ id: `custom:${query}`, name: query }]);
          });
        },
      }
    );

    runInAction(() => {
      if (requestId === this.activeSearchRequestId) {
        this.activeSearchRequestId = null;
      }
    });
  }
}

export class AdditionalInfoStore {
  aboutYourself = '';
  videoCvLink = '';
  visibility: VisibilityOption | null = null;
  driverLicense = false;

  readonly skills = new MultiSelectViewModel<SelectedOption>([], {
    maxSelected: 12,
    getLabel: (value) => value.name,
  });
  readonly driverCategories = new MultiSelectViewModel<SelectedOption>([], {
    getLabel: (value) => value.name,
  });
  readonly languages = observable.array<LanguageItem>([new LanguageItem()]);
  readonly skillOptions = observable.array<SelectedOption>([]);
  readonly languageOptions = observable.array<DirectoryOption>([]);
  readonly languageSkillOptions = observable.array<DirectoryOption>([]);
  readonly driverCategoryOptions = observable.array<SelectedOption>([]);
  readonly visibilityOptions = observable.array<VisibilityOption>([]);
  readonly companyWhitelist = new CompanyMultiAutocomplete();
  readonly companyBlacklist = new CompanyMultiAutocomplete();

  constructor() {
    makeObservable(this, {
      aboutYourself: observable,
      videoCvLink: observable,
      visibility: observable,
      driverLicense: observable,
      skillOptions: observable,
      languageOptions: observable,
      languageSkillOptions: observable,
      driverCategoryOptions: observable,
      visibilityOptions: observable,
      valid: computed,
      filteredSkills: computed,
      filteredDriverCategories: computed,
      selectedLanguagesValid: computed,
      isWhitelistVisibility: computed,
      isBlacklistVisibility: computed,
      setAboutYourself: action.bound,
      setVideoCvLink: action.bound,
      setDriverLicense: action.bound,
      setVisibility: action.bound,
      addLanguage: action.bound,
      removeLanguage: action.bound,
      setLanguageId: action.bound,
      setLanguageSkillId: action.bound,
      update: action.bound,
      dispose: action.bound,
    });
  }

  get valid(): boolean {
    return Boolean(
      this.selectedLanguagesValid &&
        this.visibility &&
        (!this.driverLicense || this.driverCategories.values.length > 0) &&
        (!this.isWhitelistVisibility || this.companyWhitelist.valid) &&
        (!this.isBlacklistVisibility || this.companyBlacklist.valid)
    );
  }

  get selectedLanguagesValid(): boolean {
    return this.languages.every((language) => language.valid);
  }

  get filteredSkills(): SelectedOption[] {
    return this.skillOptions.filter((option) => matchesSearch(option, this.skills.search));
  }

  get filteredDriverCategories(): SelectedOption[] {
    return this.driverCategoryOptions.filter((option) =>
      matchesSearch(option, this.driverCategories.search)
    );
  }

  get isWhitelistVisibility(): boolean {
    return this.visibility?.value === '1';
  }

  get isBlacklistVisibility(): boolean {
    return this.visibility?.value === '2';
  }

  setAboutYourself(value: string): void {
    this.aboutYourself = value;
  }

  setVideoCvLink(value: string): void {
    this.videoCvLink = value;
  }

  setDriverLicense(value: boolean): void {
    this.driverLicense = value;

    if (!value) {
      this.driverCategories.clear();
    }
  }

  setVisibility(value: string): void {
    this.visibility = this.visibilityOptions.find((option) => option.value === value) ?? null;
    this.companyWhitelist.clear();
    this.companyBlacklist.clear();
  }

  addLanguage(): void {
    if (this.languages.length >= 5) {
      return;
    }

    this.languages.push(new LanguageItem());
  }

  removeLanguage(id: string): void {
    if (this.languages.length <= 1) {
      return;
    }

    const index = this.languages.findIndex((language) => language.id === id);

    if (index >= 0) {
      this.languages.splice(index, 1);
    }
  }

  setLanguageId(id: string, value: string): void {
    const language = this.languages.find((item) => item.id === id);
    language?.setLanguageId(value);
  }

  setLanguageSkillId(id: string, value: string): void {
    const language = this.languages.find((item) => item.id === id);
    language?.setSkillId(value);
  }

  update(
    payload: CandidateEditProfileEmpty | NewCVCandidateResponse,
    options: { hydrateProfile?: boolean } = {}
  ): void {
    const hydrateProfile = options.hydrateProfile ?? true;

    this.skillOptions.replace(
      payload.skill_list.map((skill) => ({
        id: skill.id,
        name: skill.name,
      }))
    );
    this.languageOptions.replace(payload.langs);
    this.languageSkillOptions.replace(payload.lang_skills);
    this.driverCategoryOptions.replace(
      payload.driver_categories.map((category) => ({
        id: category.id,
        name: category.name,
      }))
    );
    this.visibilityOptions.replace(
      payload.visibilities.map((visibility) => ({
        value: String(visibility.id),
        label: visibility.name,
      }))
    );

    if (!hydrateProfile) {
      this.aboutYourself = '';
      this.videoCvLink = '';
      this.skills.clear();
      this.driverLicense = false;
      this.driverCategories.clear();
      this.languages.replace([new LanguageItem()]);
      this.visibility = this.visibilityOptions.find((option) => option.value === '3') ?? null;
      this.companyWhitelist.clear();
      this.companyBlacklist.clear();
      return;
    }

    const userdetail = payload.userdetail as UserDetailWithCompanies | undefined;
    if (!userdetail) {
      return;
    }

    this.aboutYourself = userdetail.about_yourself ?? '';
    this.videoCvLink = userdetail.video_cv_link ?? '';
    this.skills.values.replace(
      userdetail.skills
        .map((skill) => toOptionWithId(skill))
        .filter((skill): skill is SelectedOption => Boolean(skill))
    );
    this.driverLicense = Boolean(userdetail.driver_license);
    this.driverCategories.values.replace(
      userdetail.driver_category
        .map((category) => toOptionWithId(category))
        .filter((category): category is SelectedOption => Boolean(category))
    );

    this.languages.replace(
      userdetail.languages.length
        ? userdetail.languages.map(
            (language) =>
              new LanguageItem({
                languageId: String(language.lng ?? ''),
                skillId: String(language.lng_skill ?? ''),
              })
          )
        : [new LanguageItem()]
    );

    this.visibility = userdetail.visibility
      ? createVisibilityOption(userdetail.visibility)
      : this.visibilityOptions.find((option) => option.value === '3') ?? null;

    this.companyWhitelist.setSelected(
      (userdetail.company_whitelist ?? [])
        .map((company) => toOptionWithId(company))
        .filter((company): company is SelectedOption => Boolean(company))
    );
    this.companyBlacklist.setSelected(
      (userdetail.company_blacklist ?? [])
        .map((company) => toOptionWithId(company))
        .filter((company): company is SelectedOption => Boolean(company))
    );
  }

  dispose(): void {
    this.companyWhitelist.dispose();
    this.companyBlacklist.dispose();
  }
}
